import React from 'react';
import {
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  FormControlLabel,
  FormControl,
  FormHelperText,
  FormGroup,
} from '@mui/material';
import { StyledCheckbox, StyledFormControl } from './Checkbox.style';

export interface CheckboxProps extends Omit<MuiCheckboxProps, 'onChange'> {
  /**
   * The label for the checkbox
   */
  label?: string;
  /**
   * Whether the checkbox is checked
   */
  checked?: boolean;
  /**
   * Whether the checkbox is indeterminate
   */
  indeterminate?: boolean;
  /**
   * Callback fired when the checkbox is toggled
   */
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Whether the checkbox is disabled
   */
  disabled?: boolean;
  /**
   * Whether the field has an error
   */
  error?: boolean;
  /**
   * Helper text to display below the checkbox
   */
  helperText?: string;
  /**
   * The size of the checkbox
   */
  size?: 'small' | 'medium';
  /**
   * The color of the checkbox
   */
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  /**
   * Whether the checkbox is required
   */
  required?: boolean;
  /**
   * Position of the label relative to the checkbox
   */
  labelPlacement?: 'start' | 'end' | 'top' | 'bottom';
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked = false,
  indeterminate = false,
  onChange,
  disabled = false,
  error = false,
  helperText,
  size = 'medium',
  color = 'primary',
  required = false,
  labelPlacement = 'end',
  ...props
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.checked, event);
    }
  };

  const checkboxElement = (
    <StyledCheckbox
      checked={checked}
      indeterminate={indeterminate}
      onChange={handleChange}
      disabled={disabled}
      size={size}
      color={color}
      required={required}
      {...props}
    />
  );

  if (!label && !helperText) {
    return checkboxElement;
  }

  return (
    <StyledFormControl error={error} disabled={disabled}>
      {label ? (
        <FormControlLabel
          control={checkboxElement}
          label={label}
          labelPlacement={labelPlacement}
          disabled={disabled}
          required={required}
        />
      ) : (
        checkboxElement
      )}
      
      {helperText && (
        <FormHelperText>
          {helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
}; 